import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Mail, MapPin, Building, User, AlertCircle, Shield, CheckCircle2, XCircle, Briefcase, Search, DollarSign, X, Wallet, TrendingDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { EmployeeFormModal } from '../components/EmployeeFormModal';
import { getEmployees, addEmployee, updateEmployee } from '../utils/companyManagement';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured, generateSecureId } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { MONTH_NAMES, EmployeeBalance, formatMonth } from '../contexts/BalanceContext';
export function ManagerEmployeesPage() {
  useDocumentTitle('Gestión de Empleados');
  const {
    user
  } = useAuth();
  const [employees, setEmployees] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  const {
    addLog
  } = useActivityLog();
  // Balance state
  const [balancesMap, setBalancesMap] = useState<Record<string, EmployeeBalance>>({});
  const [subsidyEnabled, setSubsidyEnabled] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningEmployee, setAssigningEmployee] = useState<any>(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const [assignForm, setAssignForm] = useState({
    month: currentMonth,
    year: currentYear,
    amount: '',
    notes: ''
  });
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Access Control
  if (!user?.permissions?.isManager) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center" data-id="element-4150">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-4151">
          <AlertCircle size={32} className="text-red-600" data-id="element-4152" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4153">
          Acceso Restringido
        </h2>
        <p className="text-gray-500 max-w-md" data-id="element-4154">
          No tienes permisos para ver esta sección.
        </p>
      </div>;
  }
  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  // Load companies, employees, and balances in a single pass — avoids timing issues
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        let loadedCompanies: any[] = [];
        if (isSupabaseConfigured()) {
          loadedCompanies = await supabaseClient.getCompanies();
          localStorage.setItem('criket_companies', JSON.stringify(loadedCompanies));
        } else {
          const saved = localStorage.getItem('criket_companies');
          loadedCompanies = saved ? JSON.parse(saved) : [];
        }
        setCompanies(loadedCompanies);
        const loadedEmployees = await getEmployees();
        setEmployees(loadedEmployees);
        // Resolve company ID for this manager — try name match first, then employee record
        if (isSupabaseConfigured()) {
          let companyId: any = loadedCompanies.find((c: any) => c.name?.toLowerCase().trim() === user?.company?.toLowerCase().trim())?.id;
          // Fallback: find via the manager's own employee record
          if (!companyId) {
            const myRecord = loadedEmployees.find((e: any) => e.id === user?.id || e.id === user?.employeeId);
            if (myRecord) {
              companyId = myRecord.company_id || myRecord.companyId;
            }
          }
          if (companyId) {
            const company = loadedCompanies.find((c: any) => String(c.id) === String(companyId));
            setSubsidyEnabled(company?.subsidyEnabled || false);
            if (company?.subsidyEnabled) {
              try {
                const now = new Date();
                const month = now.getMonth() + 1;
                const year = now.getFullYear();
                const balances = await supabaseClient.getCompanyBalances(String(companyId), month, year);
                const map: Record<string, EmployeeBalance> = {};
                balances.forEach((b: EmployeeBalance) => {
                  map[String(b.employeeId)] = b;
                });
                setBalancesMap(map);
              } catch (e) {
                console.error('Error loading balances:', e);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user?.id, user?.company, user?.employeeId]);
  // Real-time updates
  useEffect(() => {
    const subscription = supabaseClient.subscribeToEmployees(async () => {
      const data = await getEmployees();
      setEmployees(data);
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  // Get user's company ID — case-insensitive match + fallback via employee record
  const userCompanyId = companies.find(c => c.name?.toLowerCase().trim() === user?.company?.toLowerCase().trim())?.id;
  // Load balances and subsidy status once company is known
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    // Try to find company via name match (case-insensitive)
    const resolvedCompanyId = userCompanyId;
    // If still not found, try to get it from the current user's employee record
    const resolveAndLoad = async () => {
      let companyId = resolvedCompanyId;
      if (!companyId && user?.employeeId) {
        try {
          const allEmployees = await supabaseClient.getEmployees();
          const myRecord = allEmployees.find((e: any) => e.id === user.employeeId);
          if (myRecord) {
            companyId = myRecord.company_id || myRecord.companyId;
          }
        } catch (e) {
          console.error('Error resolving company from employee record:', e);
        }
      }
      if (!companyId) return;
      const company = companies.find(c => String(c.id) === String(companyId));
      setSubsidyEnabled(company?.subsidyEnabled || false);
      try {
        const balances = await supabaseClient.getCompanyBalances(String(companyId), currentMonth, currentYear);
        const map: Record<string, EmployeeBalance> = {};
        balances.forEach((b: EmployeeBalance) => {
          map[String(b.employeeId)] = b;
        });
        setBalancesMap(map);
      } catch (e) {
        console.error('Error loading balances:', e);
      }
    };
    resolveAndLoad();
  }, [userCompanyId, companies, currentMonth, currentYear, user?.employeeId]);
  // Filter employees for this company
  const companyEmployees = employees.filter(e => String(e.companyId || e.company_id) === String(userCompanyId));
  const filteredEmployees = companyEmployees.filter(e => {
    return e.name.toLowerCase().includes(searchTerm.toLowerCase()) || e.email.toLowerCase().includes(searchTerm.toLowerCase()) || e.username && e.username.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  const handleAddEmployee = async (newEmployee: any) => {
    try {
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, newEmployee);
        addLog('Empleado Actualizado', `${newEmployee.name} - ${user?.company}`, 'employee');
      } else {
        const username = newEmployee.username || newEmployee.name.toLowerCase().replace(/\s+/g, '.').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const employee = {
          ...newEmployee,
          id: generateSecureId('emp'),
          companyId: userCompanyId,
          username,
          permissions: {
            ...newEmployee.permissions,
            tempPassword: !editingEmployee ? true : newEmployee.permissions?.tempPassword || false
          }
        };
        await addEmployee(employee);
        addLog('Empleado Creado', `${newEmployee.name} - ${user?.company}`, 'employee');
      }
      const data = await getEmployees();
      setEmployees(data);
      setEditingEmployee(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving employee:', error);
    }
  };
  const handleEdit = (employee: any) => {
    setEditingEmployee(employee);
    setIsModalOpen(true);
  };
  const handleOpenModal = () => {
    setEditingEmployee(null);
    setIsModalOpen(true);
  };
  const handleOpenAssign = (employee: any) => {
    setAssigningEmployee(employee);
    setAssignForm({
      month: currentMonth,
      year: currentYear,
      amount: '',
      notes: ''
    });
    setAssignError('');
    setAssignModalOpen(true);
  };
  const handleAssignBalance = async () => {
    if (!assigningEmployee || !assignForm.amount) return;
    const amount = parseFloat(assignForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setAssignError('Ingresa un monto válido mayor a 0.');
      return;
    }
    setAssignLoading(true);
    setAssignError('');
    try {
      await supabaseClient.assignBalance({
        employeeId: assigningEmployee.id,
        companyId: String(userCompanyId),
        month: assignForm.month,
        year: assignForm.year,
        amount,
        assignedBy: user?.name || 'Gerente',
        notes: assignForm.notes || undefined
      });
      await supabaseClient.createBalanceLog({
        employeeId: assigningEmployee.id,
        employeeName: assigningEmployee.name,
        companyId: String(userCompanyId),
        companyName: user?.company,
        actionType: 'balance_assigned',
        amount,
        month: assignForm.month,
        year: assignForm.year,
        performedBy: user?.name || 'Gerente',
        performedByRole: 'manager',
        notes: assignForm.notes || `Balance asignado por gerente para ${formatMonth(assignForm.month, assignForm.year)}`
      });
      addLog('Balance Asignado', `${assigningEmployee.name} — RD$ ${amount.toFixed(2)} (${formatMonth(assignForm.month, assignForm.year)})`, 'employee');
      // Reload balances
      const balances = await supabaseClient.getCompanyBalances(String(userCompanyId), currentMonth, currentYear);
      const map: Record<string, EmployeeBalance> = {};
      balances.forEach((b: EmployeeBalance) => {
        map[b.employeeId] = b;
      });
      setBalancesMap(map);
      setAssignModalOpen(false);
    } catch (e: any) {
      setAssignError(e.message || 'Error al asignar balance.');
    } finally {
      setAssignLoading(false);
    }
  };
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-4155">
        <div className="flex items-center justify-center py-20" data-id="element-4156">
          <div className="text-center" data-id="element-4157">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" data-id="element-4158"></div>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-4159">
              Cargando empleados...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-10 max-w-7xl mx-auto space-y-5 lg:space-y-8" data-id="element-4160">
      {/* Header — compact on mobile */}
      <div className="flex items-center justify-between gap-3" data-id="element-4161">
        <div className="min-w-0" data-id="element-4162">
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900 dark:text-white tracking-tight truncate" data-id="element-4163">
            Empleados
            <span className="hidden sm:inline" data-id="element-4164"> de {user?.company}</span>
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm hidden sm:block mt-1" data-id="element-4165">
            Gestiona los empleados de tu empresa
          </p>
        </div>
        <Button leftIcon={<Plus size={16} data-id="element-4167" />} onClick={handleOpenModal} size="md" className="shrink-0 shadow-sm" data-id="element-4166">
          <span className="hidden sm:inline" data-id="element-4168">Nuevo </span>Empleado
        </Button>
      </div>

      {/* Stats Cards — 3 columns always (compact on mobile) */}
      <div className="grid grid-cols-3 gap-2 lg:gap-6" data-id="element-4169">
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3 lg:p-5 flex flex-col items-start gap-1.5 lg:gap-4 shadow-sm relative overflow-hidden" data-id="element-4170">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" data-id="element-4171" />
          <div className="p-1.5 lg:p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg shrink-0" data-id="element-4172">
            <User className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600 dark:text-blue-400" data-id="element-4173" />
          </div>
          <div data-id="element-4174">
            <p className="text-[9px] lg:text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider leading-none mb-1" data-id="element-4175">
              Total
            </p>
            <p className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white leading-none" data-id="element-4176">
              {companyEmployees.length}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3 lg:p-5 flex flex-col items-start gap-1.5 lg:gap-4 shadow-sm relative overflow-hidden" data-id="element-4177">
          <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" data-id="element-4178" />
          <div className="p-1.5 lg:p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg shrink-0" data-id="element-4179">
            <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-600 dark:text-emerald-400" data-id="element-4180" />
          </div>
          <div data-id="element-4181">
            <p className="text-[9px] lg:text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider leading-none mb-1" data-id="element-4182">
              Activos
            </p>
            <p className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white leading-none" data-id="element-4183">
              {companyEmployees.filter(e => e.status === 'Active').length}
            </p>
          </div>
        </div>

        {subsidyEnabled ? <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3 lg:p-5 flex flex-col items-start gap-1.5 lg:gap-4 shadow-sm relative overflow-hidden" data-id="element-4184">
            <div className="absolute top-0 left-0 w-1 h-full bg-orange-500" data-id="element-4185" />
            <div className="p-1.5 lg:p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg shrink-0" data-id="element-4186">
              <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 text-orange-600 dark:text-orange-400" data-id="element-4187" />
            </div>
            <div data-id="element-4188">
              <p className="text-[9px] lg:text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider leading-none mb-1" data-id="element-4189">
                Balance
              </p>
              <p className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white leading-none" data-id="element-4190">
                {Object.keys(balancesMap).length}
              </p>
            </div>
          </div> : <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3 lg:p-5 flex flex-col items-start gap-1.5 lg:gap-4 shadow-sm relative overflow-hidden" data-id="element-4191">
            <div className="absolute top-0 left-0 w-1 h-full bg-purple-500" data-id="element-4192" />
            <div className="p-1.5 lg:p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg shrink-0" data-id="element-4193">
              <Building className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600 dark:text-purple-400" data-id="element-4194" />
            </div>
            <div data-id="element-4195">
              <p className="text-[9px] lg:text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider leading-none mb-1" data-id="element-4196">
                Sucursales
              </p>
              <p className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white leading-none" data-id="element-4197">
                {new Set(companyEmployees.map(e => e.branchId)).size}
              </p>
            </div>
          </div>}
      </div>

      {subsidyEnabled && <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-xl shadow-sm" data-id="element-4198">
          <div className="p-1.5 bg-emerald-100 dark:bg-emerald-800 rounded-full" data-id="element-4199">
            <DollarSign size={14} className="text-emerald-700 dark:text-emerald-300" data-id="element-4200" />
          </div>
          <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100" data-id="element-4201">
            Subsidio activo — Asigna balance para{' '}
            <span className="font-bold" data-id="element-4202">
              {MONTH_NAMES[currentMonth - 1]} {currentYear}
            </span>
          </p>
        </div>}

      {/* Search Bar - Direct, no extra card */}
      <SearchBar placeholder="Buscar por nombre, email o usuario..." className="w-full shadow-sm" onSearch={setSearchTerm} data-id="element-4203" />

      {filteredEmployees.length === 0 ? <div className="text-center py-24 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-4204">
          <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4205">
            <User size={32} className="text-gray-400" data-id="element-4206" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4207">
            No se encontraron empleados
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-4208">
            {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Agrega tu primer empleado para comenzar'}
          </p>
        </div> : <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm" data-id="element-4209">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto" data-id="element-4210">
            <table className="w-full text-left border-collapse min-w-[900px]" data-id="element-4211">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800" data-id="element-4212">
                <tr data-id="element-4213">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4214">
                    Empleado
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4215">
                    Usuario
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4216">
                    Cargo
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4217">
                    Sucursal
                  </th>
                  {subsidyEnabled && <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4218">
                      Balance {MONTH_NAMES[currentMonth - 1]}
                    </th>}
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4219">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-4220">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-4221">
                {paginatedEmployees.map(employee => {
              const company = companies.find(c => c.id === employee.companyId);
              const branch = company?.branches?.find((b: any) => b.id === employee.branchId || b.name === employee.branchId);
              const balance = balancesMap[String(employee.id)];
              return <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-4222">
                      <td className="px-6 py-4" data-id="element-4223">
                        <div className="flex items-center gap-4" data-id="element-4224">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-sm shrink-0 border border-gray-200 dark:border-gray-700" data-id="element-4225">
                            {employee.name.charAt(0)}
                          </div>
                          <div data-id="element-4226">
                            <div className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-4227">
                              {employee.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5" data-id="element-4228">
                              <Mail size={10} data-id="element-4229" /> {employee.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-4230">
                        <div className="flex items-center gap-2" data-id="element-4231">
                          <Shield size={14} className="text-gray-400" data-id="element-4232" />
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded" data-id="element-4233">
                            {employee.username || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-4234">
                        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-4235">
                          <Briefcase size={14} className="text-gray-400" data-id="element-4236" />
                          {employee.position}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-4237">
                        <div className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5" data-id="element-4238">
                          <MapPin size={14} className="text-gray-400" data-id="element-4239" />
                          {branch?.name || 'N/A'}
                        </div>
                      </td>
                      {subsidyEnabled && <td className="px-6 py-4" data-id="element-4240">
                          {balance ? <div className="space-y-1" data-id="element-4241">
                              <div className="flex items-center gap-1.5" data-id="element-4242">
                                <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400" data-id="element-4243">
                                  RD$ {balance.currentBalance.toFixed(2)}
                                </span>
                                <span className="text-xs text-gray-400" data-id="element-4244">
                                  / {balance.assignedAmount.toFixed(2)}
                                </span>
                              </div>
                              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4245">
                                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{
                        width: `${Math.min(100, balance.currentBalance / balance.assignedAmount * 100)}%`
                      }} data-id="element-4246" />
                              </div>
                            </div> : <span className="text-xs text-gray-400 italic" data-id="element-4247">
                              Sin asignar
                            </span>}
                        </td>}
                      <td className="px-6 py-4" data-id="element-4248">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-full border ${employee.status === 'Active' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'}`} data-id="element-4249">
                          {employee.status === 'Active' ? <CheckCircle2 size={12} data-id="element-4250" /> : <XCircle size={12} data-id="element-4251" />}
                          {employee.status === 'Active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-4252">
                        <div className="flex items-center justify-end gap-1" data-id="element-4253">
                          {subsidyEnabled && employee.status === 'Active' && isAdmin && <button onClick={() => handleOpenAssign(employee)} className={`p-2 rounded-lg transition-colors ${balance ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : 'text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'}`} title={balance ? 'Modificar Balance' : 'Asignar Balance'} data-id="element-4254">
                                <DollarSign size={16} data-id="element-4255" />
                              </button>}
                          <button onClick={() => handleEdit(employee)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-4256">
                            <Edit2 size={16} data-id="element-4257" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Compact list rows */}
          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4258">
            {paginatedEmployees.map(employee => {
          const company = companies.find(c => c.id === employee.companyId);
          const branch = company?.branches?.find((b: any) => b.id === employee.branchId || b.name === employee.branchId);
          const balance = balancesMap[String(employee.id)];
          const isActive = employee.status === 'Active';
          return <div key={employee.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors" data-id="element-4259">
                  {/* Avatar with status dot */}
                  <div className="relative shrink-0" data-id="element-4260">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-700 dark:text-gray-200 font-bold text-sm border border-gray-200 dark:border-gray-700" data-id="element-4261">
                      {employee.name.charAt(0).toUpperCase()}
                    </div>
                    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-900 ${isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-4262" />
                  </div>

                  {/* Name + subtitle */}
                  <div className="flex-1 min-w-0" data-id="element-4263">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight" data-id="element-4264">
                      {employee.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" data-id="element-4265">
                      {employee.position}
                      {branch?.name ? ` · ${branch.name}` : ''}
                    </p>
                    {/* Balance pill — only when subsidy enabled */}
                    {subsidyEnabled && <div className="mt-1" data-id="element-4266">
                        {balance ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full" data-id="element-4267">
                            <Wallet size={10} data-id="element-4268" />
                            RD$ {balance.currentBalance.toFixed(0)}
                          </span> : <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full italic" data-id="element-4269">
                            Sin balance
                          </span>}
                      </div>}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 shrink-0" data-id="element-4270">
                    {subsidyEnabled && isActive && isAdmin && <button onClick={() => handleOpenAssign(employee)} className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${balance ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100' : 'text-gray-500 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'}`} title={balance ? 'Modificar Balance' : 'Asignar Balance'} data-id="element-4271">
                        <DollarSign size={16} data-id="element-4272" />
                      </button>}
                    <button onClick={() => handleEdit(employee)} className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-500 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" title="Editar" data-id="element-4273">
                      <Edit2 size={15} data-id="element-4274" />
                    </button>
                  </div>
                </div>;
        })}
          </div>

          {totalPages > 1 && <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-4275">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredEmployees.length} itemsPerPage={itemsPerPage} data-id="element-4276" />
            </div>}
        </div>}

      {/* Employee Form Modal */}
      <EmployeeFormModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingEmployee(null);
    }} onSave={handleAddEmployee} editingEmployee={editingEmployee} lockedCompanyId={userCompanyId} managerMode={true} data-id="element-4277" />

      {/* Assign Balance Modal */}
      {assignModalOpen && assigningEmployee && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-4278">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4279">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-4280">
              <div data-id="element-4281">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4282">
                  Asignar Balance
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-4283">
                  {assigningEmployee.name}
                </p>
              </div>
              <button onClick={() => setAssignModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-4284">
                <X size={18} data-id="element-4285" />
              </button>
            </div>
            <div className="p-6 space-y-5" data-id="element-4286">
              <div className="grid grid-cols-2 gap-4" data-id="element-4287">
                <div data-id="element-4288">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-4289">
                    Mes
                  </label>
                  <select value={assignForm.month} onChange={e => setAssignForm(f => ({
                ...f,
                month: parseInt(e.target.value)
              }))} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-4290">
                    {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1} data-id="element-4291">
                        {m}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-4292">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-4293">
                    Año
                  </label>
                  <input type="number" value={assignForm.year} onChange={e => setAssignForm(f => ({
                ...f,
                year: parseInt(e.target.value)
              }))} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" min={2024} max={2099} data-id="element-4294" />
                </div>
              </div>
              <div data-id="element-4295">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-4296">
                  Monto (RD$)
                </label>
                <div className="relative" data-id="element-4297">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500" data-id="element-4298">
                    RD$
                  </span>
                  <input type="number" step="0.01" min="0.01" value={assignForm.amount} onChange={e => setAssignForm(f => ({
                ...f,
                amount: e.target.value
              }))} placeholder="0.00" className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono" data-id="element-4299" />
                </div>
              </div>
              <div data-id="element-4300">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-4301">
                  Notas (opcional)
                </label>
                <input type="text" value={assignForm.notes} onChange={e => setAssignForm(f => ({
              ...f,
              notes: e.target.value
            }))} placeholder="Ej. Subsidio mensual estándar" className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-4302" />
              </div>
              {assignError && <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-4303">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-4304" />
                  <p className="text-sm text-red-700 dark:text-red-300" data-id="element-4305">
                    {assignError}
                  </p>
                </div>}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-4306">
              <Button variant="outline" onClick={() => setAssignModalOpen(false)} fullWidth data-id="element-4307">
                Cancelar
              </Button>
              <Button onClick={handleAssignBalance} disabled={assignLoading || !assignForm.amount} fullWidth data-id="element-4308">
                {assignLoading ? 'Asignando...' : 'Asignar Balance'}
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}